// Utility function to parse query parameters from the URL
export const getQueryParams = (): Record<string, string> => {
  const params: Record<string, string> = {};
  
  // Get the current URL's query string
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  
  // Convert URLSearchParams to a plain object
  urlParams.forEach((value, key) => {
    params[key] = value;
  });
  
  return params;
};

// Constants for local storage keys
const STORAGE_KEY_PREFIX = 'rri_param_';
const MARKETING_PARAM_KEYS = ['partnerCode', 'mbsy', 'campaignid'];

// Store marketing parameters in local storage
export const storeMarketingParams = (params: Record<string, string>): void => {
  // Store known marketing parameters individually
  MARKETING_PARAM_KEYS.forEach(key => {
    if (params[key]) {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, params[key]);
      
      // Also store mbsy under its direct key for compatibility with other pages
      if (key === 'mbsy') {
        localStorage.setItem(key, params[key]);
      }
    }
  });
  
  // Store all other query parameters in a single JSON string
  const otherParams: Record<string, string> = {};
  let hasOtherParams = false;
  
  Object.keys(params).forEach(key => {
    if (!MARKETING_PARAM_KEYS.includes(key)) {
      otherParams[key] = params[key];
      hasOtherParams = true;
    }
  });
  
  if (hasOtherParams) {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}qs`, JSON.stringify(otherParams));
  }
};

// Function to call on form/app load to store marketing params from URL
export const storeMarketingParamsOnLoad = (): void => {
  const urlParams = getQueryParams();
  storeMarketingParams(urlParams);
};

// Get marketing parameters from local storage
const getStoredMarketingParams = (): Record<string, string> => {
  const storedParams: Record<string, string> = {};
  
  // Get specific marketing parameters
  MARKETING_PARAM_KEYS.forEach(key => {
    // Try prefixed key first
    let value = localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
    
    // For mbsy, if not found with prefix, try direct key
    if (!value && key === 'mbsy') {
      value = localStorage.getItem(key);
      
      // If found with direct key, also store with our prefix for consistency
      if (value) {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, value);
      }
    }
    
    if (value) {
      storedParams[key] = value;
    }
  });
  
  // Get additional query parameters stored in the JSON string
  const qsValue = localStorage.getItem(`${STORAGE_KEY_PREFIX}qs`);
  if (qsValue) {
    try {
      const qsParams = JSON.parse(qsValue);
      Object.keys(qsParams).forEach(key => {
        // Don't overwrite specific marketing params if they exist
        if (!storedParams[key]) {
          storedParams[key] = qsParams[key];
        }
      });
    } catch (e) {
      console.error('Error parsing stored query parameters:', e);
    }
  }
  
  return storedParams;
};

// Get all marketing and query parameters we're interested in
export const getMarketingParams = (): Record<string, string> => {
  const urlParams = getQueryParams();
  const storedParams = getStoredMarketingParams();
  
  // Store any new marketing parameters from URL
  storeMarketingParams(urlParams);
  
  const result: Record<string, string> = { ...storedParams };
  
  // URL parameters take precedence over stored parameters
  Object.keys(urlParams).forEach(key => {
    result[key] = urlParams[key];
  });
  
  return result;
};